import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CatalogOffering, CatalogService, CatalogWarehouse } from './catalog.service';
import { InventoryCostingApiService, InventoryValuationSnapshot } from './inventory-costing-api.service';

@Component({
  standalone: true,
  selector: 'ain-inventory-valuation-snapshot',
  imports: [AinPageComponent, AinStateComponent, InfoHintComponent, RouterLink, TableModule, ButtonModule],
  template: `
    <ain-page [title]="t('catalog.costing.valuation.snapshotTitle')" [description]="t('catalog.costing.valuation.snapshotHelp.description')">
      <ain-info-hint page-title-addon
        [titleKey]="'catalog.costing.valuation.snapshotHelp.title'"
        [descriptionKey]="'catalog.costing.valuation.snapshotHelp.description'" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('catalog.loading')" />
      } @else if (!snapshot()) {
        <ain-state type="error" [title]="t('catalog.costing.valuation.snapshotNotFound')" />
      } @else {
        <section class="panel">
          <a [routerLink]="['/inventory/costing/valuation']" class="back-link">{{ t('catalog.action.backToList') }}</a>
          <div class="summary">
            <div><strong>{{ t('catalog.costing.valuation.capturedAt') }}:</strong> {{ snapshot()!.capturedAtUtc }}</div>
            <div><strong>{{ t('columns.currency') }}:</strong> {{ snapshot()!.currencyCode }}</div>
            <div><strong>{{ t('catalog.costing.valuation.totalQuantity') }}:</strong> {{ snapshot()!.totalQuantity }}</div>
            <div><strong>{{ t('catalog.costing.valuation.totalCost') }}:</strong> {{ snapshot()!.totalCost }}</div>
          </div>
          <p-table [value]="snapshot()!.lines" [paginator]="true" [rows]="20">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('catalog.inventory.offeringId') }}</th>
                <th>{{ t('catalog.inventory.warehouseId') }}</th>
                <th>{{ t('catalog.inventory.quantity') }}</th>
                <th>{{ t('catalog.costing.valuation.totalCost') }}</th>
                <th>{{ t('catalog.costing.valuation.avgUnitCost') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr>
                <td>{{ resolveOffering(row.offeringId) }}</td>
                <td>{{ resolveWarehouse(row.warehouseId) }}</td>
                <td>{{ row.quantity }}</td>
                <td>{{ row.totalCost }}</td>
                <td>{{ row.averageUnitCost }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .panel { border: 1px solid #e2e8f0; border-radius: 12px; padding: 12px; background: #fff; }
    .back-link { color: #1d4ed8; font-weight: 600; text-decoration: none; display: inline-block; margin-bottom: 10px; }
    .summary { display: grid; gap: 6px; margin-bottom: 12px; }
  `]
})
export class InventoryValuationSnapshotComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly costingApi = inject(InventoryCostingApiService);
  private readonly catalog = inject(CatalogService);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly snapshot = signal<InventoryValuationSnapshot | null>(null);
  readonly offerings = signal<CatalogOffering[]>([]);
  readonly warehouses = signal<CatalogWarehouse[]>([]);

  ngOnInit(): void {
    this.catalog.listOfferings().subscribe({ next: o => this.offerings.set(o) });
    this.catalog.listWarehouses().subscribe({ next: w => this.warehouses.set(w) });
    const id = this.route.snapshot.paramMap.get('id') ?? '';
    this.costingApi.getValuationSnapshot(id).subscribe({
      next: s => { this.snapshot.set(s); this.loading.set(false); },
      error: () => { this.snapshot.set(null); this.loading.set(false); }
    });
  }

  resolveOffering(id: string): string {
    const o = this.offerings().find(x => x.id === id);
    return o ? `${o.code} — ${o.title}` : '-';
  }

  resolveWarehouse(id: string): string {
    const w = this.warehouses().find(x => x.id === id);
    return w ? `${w.code} — ${w.name}` : '-';
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
